import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingIndicator from '../common/LoadingIndicator';
import FormAction from './FormAction';
import { LoadingStates as states } from '../constants/states';
import {
  buttonTypes,
  challengeFormats,
  challengeTypes,
  configurationKeys,
} from '../constants/clientConstants';
import { useTranslation } from 'react-i18next';
import PinInput from 'react-pin-input';
import ErrorBanner from '../common/ErrorBanner';
import langConfigService from '../services/langConfigService';
import redirectOnError from '../helpers/redirectOnError';
import { isMobileOtpAutoFillDevice } from '../services/otpAutoFill';

export default function OtpVerify({
  otpResponse,
  ID,
  authService,
  openIDConnectService,
  i18nKeyPrefix1 = 'otp',
  i18nKeyPrefix2 = 'errors',
}) {
  const { t: t1, i18n } = useTranslation('translation', {
    keyPrefix: i18nKeyPrefix1,
  });
  const { t: t2 } = useTranslation('translation', {
    keyPrefix: i18nKeyPrefix2,
  });

  const [langConfig, setLangConfig] = useState(null);

  useEffect(() => {
    async function loadLangConfig() {
      try {
        const config = await langConfigService.getEnLocaleConfiguration();
        setLangConfig(config);
      } catch (e) {
        console.error('Failed to load lang config', e);
        setLangConfig({ errors: { otp: {} } });
      }
    }

    loadLangConfig();
  }, []);

  const post_AuthenticateUser = authService.post_AuthenticateUser;
  const buildRedirectParams = authService.buildRedirectParamsV2;

  const otpLengthValue =
    openIDConnectService.getEsignetConfiguration(configurationKeys.otpLength) ??
    process.env.REACT_APP_OTP_LENGTH;
  const otpLength = parseInt(otpLengthValue, 10);

  const [status, setStatus] = useState({ state: states.LOADED, msg: '' });
  const [otpValue, setOtpValue] = useState('');
  const [pinInitialValue, setPinInitialValue] = useState('');
  const [pinInputKey, setPinInputKey] = useState(0);
  const [otpSentEmail, setOtpSentEmail] = useState('');
  const [otpSentMobile, setOtpSentMobile] = useState('');
  const [errorBanner, setErrorBanner] = useState(null);

  const pinRef = useRef(null);
  const otpAutoFillInputRef = useRef(null);
  const navigate = useNavigate();
  const useMobileOtpInput = isMobileOtpAutoFillDevice();

  const fullLoginId = `${ID.prefix || ''}${ID.id || ''}${ID.postfix || ''}`;

  const applyOtpCode = useCallback(
    (raw) => {
      const code = String(raw ?? '')
        .replace(/\D/g, '')
        .slice(0, otpLength);
      if (!code) return;
      setPinInitialValue(code);
      setPinInputKey((key) => key + 1);
      setOtpValue(code);
    },
    [otpLength]
  );

  const handleMobileOtpChange = (event) => {
    const code = event.target.value.replace(/\D/g, '').slice(0, otpLength);
    setOtpValue(code);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    authenticateUser();
  };

  useEffect(() => {
    setErrorBanner(null);
    setOtpSentMobile(otpResponse.maskedMobile);
    setOtpSentEmail(otpResponse.maskedEmail);
  }, [otpResponse]);

  useEffect(() => {
    if (!useMobileOtpInput) return;

    const focusFrame = requestAnimationFrame(() => {
      otpAutoFillInputRef.current?.focus({ preventScroll: true });
    });

    return () => cancelAnimationFrame(focusFrame);
  }, [useMobileOtpInput, pinInputKey]);

  useEffect(() => {
    const container = document.getElementById('otp_verify_input');
    if (!container || useMobileOtpInput) return;

    const onPaste = (event) => {
      const text = event.clipboardData?.getData('text') ?? '';
      const code = text.replace(/\D/g, '').slice(0, otpLength);
      if (code.length >= otpLength) {
        event.preventDefault();
        applyOtpCode(code);
      }
    };

    container.addEventListener('paste', onPaste);
    return () => container.removeEventListener('paste', onPaste);
  }, [applyOtpCode, otpLength, useMobileOtpInput, pinInputKey]);

  const getOtpFieldLabel = (t1) => {
    const label = t1('otp_label_text');
    return label && label !== 'otp_label_text' ? label : 'Code';
  };

  const authenticateUser = async () => {
    try {
      let transactionId = openIDConnectService.getTransactionId();

      let challengeType = challengeTypes.otp;
      let challenge = otpValue;
      let challengeFormat = challengeFormats.otp;

      let challengeList = [
        {
          authFactorType: challengeType,
          challenge: challenge,
          format: challengeFormat,
        },
      ];

      let id = ID.prefix + ID.id + ID.postfix;

      setStatus({ state: states.LOADING, msg: 'authenticating_msg' });
      const authenticateResponse = await post_AuthenticateUser(
        transactionId,
        id,
        challengeList
      );
      setStatus({ state: states.LOADED, msg: '' });

      const { response, errors } = authenticateResponse;

      if (errors !== null && errors.length > 0) {
        let errorCodeCondition =
          langConfig.errors.otp[errors[0].errorCode] !== undefined &&
          langConfig.errors.otp[errors[0].errorCode] !== null;

        let fieldLabel = getOtpFieldLabel(t1);

        if (errorCodeCondition) {
          setErrorBanner({
            errorCode: `otp.${errors[0].errorCode}`,
            show: true,
            field: fieldLabel,
          });
        } else if (errors[0].errorCode === 'invalid_transaction') {
          redirectOnError(errors[0].errorCode, t2(`${errors[0].errorCode}`));
        } else {
          setErrorBanner({
            errorCode: `${errors[0].errorCode}`,
            show: true,
            field: fieldLabel,
          });
        }
        return;
      } else {
        setErrorBanner(null);
        let params = buildRedirectParams({
          nonce: openIDConnectService.getNonce(),
          state: openIDConnectService.getState(),
          oauthResponse: openIDConnectService.getOAuthDetails(),
          consentAction: response.consentAction,
          ui_locales: i18n.language,
        });

        navigate(process.env.PUBLIC_URL + '/claim-details' + params, {
          replace: true,
        });
      }
    } catch (error) {
      setErrorBanner({
        errorCode: 'authentication_failed_msg',
        show: true,
      });
      setStatus({ state: states.ERROR, msg: '' });
    }
  };

  let styles = {
    width: '40px',
    height: '40px',
    margin: '0 5px',
    border: '',
    borderBottom: '2px solid #FA9C3D',
    color: '#FA9C3D',
    fontFamily: 'Inter, sans-serif',
  };

  if (window.screen.availWidth <= 375) {
    styles = { ...styles, width: '2em' };
  }

  const onCloseHandle = () => {
    setErrorBanner(null);
  };

  const translateOtpMessage = (key, values, fallback) => {
    const text = t1(key, values);
    if (!text || text === key || text.endsWith(`.${key}`)) {
      return fallback;
    }
    return text;
  };

  const renderDeliveryMessage = () => {
    if (status.state === states.LOADING) {
      return null;
    }

    if (otpSentMobile && otpSentEmail) {
      return translateOtpMessage(
        'otp_sent_channels_msg',
        { mobile: otpSentMobile, email: otpSentEmail },
        `Code has been sent to your registered Mobile Number: ${otpSentMobile} and Email Address: ${otpSentEmail}`
      );
    }

    if (otpSentMobile) {
      return translateOtpMessage(
        'otp_sent_mobile_msg',
        { mobile: otpSentMobile },
        `Code has been sent to your registered Mobile Number: ${otpSentMobile}`
      );
    }

    if (otpSentEmail) {
      return translateOtpMessage(
        'otp_sent_email_msg',
        { email: otpSentEmail },
        `Code has been sent to your registered Email Address: ${otpSentEmail}`
      );
    }

    return translateOtpMessage(
      'otp_sent_msg',
      { otpLength },
      `Please enter the ${otpLength}-digit Code sent to your registered contact.`
    );
  };

  return (
    <>
      {errorBanner !== null && (
        <ErrorBanner
          showBanner={errorBanner.show}
          errorCode={t2(errorBanner.errorCode, {
            field: errorBanner.field,
          })}
          onCloseHandle={onCloseHandle}
        />
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-5">
          <input
            type="text"
            readOnly
            value={fullLoginId}
            id="otp_login_id_display"
            className="w-full h-11 border border-[#D0D5DD] bg-[#F8F8F8] px-3.5 py-2.5 text-sm text-gray-900 text-center"
            aria-label={t1('vid_label_text')}
          />
        </div>

        <div
          className="relative space-y-px flex justify-center mb-6"
          id="otp_verify_input"
        >
          {useMobileOtpInput ? (
            <input
              ref={otpAutoFillInputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              name="one-time-code"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              maxLength={otpLength}
              value={otpValue}
              aria-label={t1('verify')}
              disabled={status.state === states.LOADING}
              className="h-12 w-full max-w-[min(100%,18rem)] border-0 border-b-2 border-[#FA9C3D] bg-transparent text-center text-xl font-semibold tracking-[0.45em] text-[#FA9C3D] outline-none focus:border-[#E88D2E]"
              onChange={handleMobileOtpChange}
              onInput={handleMobileOtpChange}
            />
          ) : (
            <PinInput
              key={`otp-pin-${pinInputKey}`}
              length={otpLength}
              initialValue={pinInitialValue}
              onChange={(value) => {
                setOtpValue(value);
              }}
              secret
              secretDelay={1}
              type="numeric"
              inputMode="number"
              style={{ padding: '5px 0px' }}
              inputStyle={styles}
              inputFocusStyle={{ borderBottom: '2px solid #E88D2E' }}
              onComplete={(value) => {
                setOtpValue(value);
              }}
              autoSelect={true}
              disabled={status.state === states.LOADING}
              focus={true}
              ref={(instance) => {
                pinRef.current = instance;
              }}
            />
          )}
        </div>

        <div className="text-center break-words mb-6 px-2">
          <p className="text-sm text-gray-500 leading-relaxed">
            {renderDeliveryMessage()}
          </p>
        </div>

        <FormAction
          disabled={
            otpValue.length !== otpLength || status.state === states.LOADING
          }
          type={buttonTypes.submit}
          text={t1('verify')}
          id="verify_otp"
        />

        {status.state === states.LOADING && (
          <LoadingIndicator size="medium" message={status.msg} />
        )}
      </form>
    </>
  );
}
